import { ButtonVariant } from '@packages/ui/components/buttons/types';
import { font, FontWeight } from '@packages/ui/theme/font';
import { LinkTarget } from '../../SmartLink';
import { CustomizableText, DYCustomizableText } from '../types';

/** for converting a text-field dy-template-variable to an array of numbers. DY
 * does not provide a way to define a list of items. So we need to parse it
 * ourselves. Not restricted to a specific delimiter. All the digits that are
 * together, are considered one number and everything that is not a digit gets
 * thrown out. So it can be used with spaces or commas or anything else, that is
 * not a digit
 * @example transformResponsiveDYNumbersVariable('1, 2, 3, 4') -> [1,2,3,4]
 * transformResponsiveDYNumbersVariable('1 2 3 4') -> [1,2,3,4]
 * transformResponsiveDYNumbersVariable('1.23 50, 128') -> [1.23, 50, 128]
 * transformResponsiveDYNumbersVariable('1.23 | 50~128') -> [1.23, 50, 128]
 * */
export function transformResponsiveDYNumbersVariable(dyVar: string): (number | string)[] {
  const matches = dyVar.match(/\d*\.?\d+(px)?/g);
  if (!matches) {
    return [];
  }

  return matches.reduce((acc, match) => {
    const num = Number(match);
    if (num) {
      acc.push(num);
    }

    return acc;
  }, new Array<number>());
}

export function transformCustomizableText(dyCustomizableText: DYCustomizableText): CustomizableText {
  return {
    text: dyCustomizableText.text,
    color: dyCustomizableText.color,
    size: transformResponsiveDYNumbersVariable(dyCustomizableText.size),
  };
}

export function transformButtonVariant(dyButtonVariant: string): ButtonVariant {
  const normalizedDyButtonVariant = (dyButtonVariant || '').toLocaleLowerCase().trim();
  const validVariants = ['primary', 'secondary', 'tertiary', 'tag', 'clean'];
  if (validVariants.includes(normalizedDyButtonVariant)) {
    return normalizedDyButtonVariant as ButtonVariant;
  }

  return 'primary';
}

export function transformFontWeight(dyFontWeight: string): FontWeight {
  const normalizedDyFontWeight = (dyFontWeight || '').toLocaleLowerCase().trim();
  const validKeys = Object.keys(font.fontWeights);
  if (validKeys.includes(normalizedDyFontWeight)) {
    return normalizedDyFontWeight as FontWeight;
  }

  return 'normal';
}

export function transformLinkTarget(dyLinkTarget: string): LinkTarget {
  const normalizedDyLinkTarget = (dyLinkTarget || '').toLocaleLowerCase().trim();
  const validLinkTargets = ['_blank', '_self'];
  if (validLinkTargets.includes(normalizedDyLinkTarget)) {
    return normalizedDyLinkTarget as LinkTarget;
  }

  return '_self';
}
